import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { interval, Subject, BehaviorSubject, Subscription, Observable } from 'rxjs';
import { switchMap, map, catchError, filter } from 'rxjs/operators';
import { of } from 'rxjs';

interface VersionInfo {
    version: string;
    timestamp: string;
}

/**
 * Configuration constants for the update service
 */
const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000; // Check every 5 minutes

@Injectable({
    providedIn: 'root'
})
export class UpdateService {
    private currentVersion: string | null = null;
    private updateAvailable$ = new BehaviorSubject<boolean>(false);
    private checkSubscription: Subscription | null = null;
    private initSubscription: Subscription | null = null;
    private readonly versionPath: string;

    constructor(private http: HttpClient, @Inject(LOCALE_ID) private locale: string) {
        // Construct the version.json path based on the locale
        // Angular's LOCALE_ID is typically 'en-US' or 'fr', so we take the first 2 chars
        const lang = this.locale.substring(0, 2);
        this.versionPath = `/${lang}/assets/version.json`;
    }

    /**
     * Initialize the update checker
     * This should be called once when the app starts
     */
    initialize(): void {
        // Load the current version
        this.initSubscription = this.loadCurrentVersion().subscribe(version => {
            this.currentVersion = version;
            // Start periodic checks
            this.startPeriodicChecks();
        });
    }

    /**
     * Clean up subscriptions
     */
    destroy(): void {
        if (this.initSubscription) {
            this.initSubscription.unsubscribe();
            this.initSubscription = null;
        }
        if (this.checkSubscription) {
            this.checkSubscription.unsubscribe();
            this.checkSubscription = null;
        }
    }

    /**
     * Load the current version from version.json
     */
    private loadCurrentVersion(): Observable<string> {
        return this.http.get<VersionInfo>(this.getVersionPath()).pipe(
            map(versionInfo => versionInfo.version),
            catchError(error => {
                console.error('Failed to load current version:', error);
                return of(null);
            })
        );
    }

    private getVersionPath(): string {
        const cacheBuster = new Date().getTime();
        return `${this.versionPath}?t=${cacheBuster}`;
    }

    /**
     * Check for updates by fetching version.json with cache busting
     */
    private checkForUpdate(): Observable<boolean> {
        if (!this.currentVersion) {
            return of(false);
        }
        return this.http.get<VersionInfo>(this.getVersionPath()).pipe(
            map(versionInfo => versionInfo.version !== this.currentVersion),
            catchError(error => {
                console.error('Failed to check for updates:', error);
                return of(false);
            })
        );
    }

    /**
     * Start periodic checks for updates
     */
    private startPeriodicChecks(): void {
        this.checkSubscription = interval(UPDATE_CHECK_INTERVAL_MS).pipe(
            switchMap(() => this.checkForUpdate())
        ).subscribe(updateAvailable => {
            if (updateAvailable) {
                this.updateAvailable$.next(true);
            }
        });
    }

    /**
     * Observable that emits when an update is available
     */
    get updateAvailable(): Observable<boolean> {
        return this.updateAvailable$.asObservable().pipe(
            filter(Boolean)
        );
    }

    /**
     * Reload the application to apply the update
     */
    applyUpdate(): void {
        this.performReload();
    }

    /**
     * Perform the actual reload - extracted for testability
     */
    protected performReload(): void {
        window.location.reload();
    }
}
